import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography, Container } from "@mui/material";
import {
  ErrorOutlineOutlined,
  RefreshOutlined,
  HomeOutlined,
} from "@mui/icons-material";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              gap: 3,
              textAlign: "center",
            }}
          >
            <ErrorOutlineOutlined sx={{ fontSize: 80, color: "error.main" }} />
            <Typography variant="h4" color="error">
              Something went wrong
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              maxWidth="400px"
            >
              An unexpected error occurred. Please try refreshing the page or
              return to the home page.
            </Typography>
            {import.meta.env.DEV && this.state.error && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  maxWidth: "100%",
                  overflow: "auto",
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", textAlign: "left" }}
                >
                  {this.state.error.message}
                  {this.state.error.stack && (
                    <>
                      {"\n\n"}
                      {this.state.error.stack}
                    </>
                  )}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={this.handleRetry}
              >
                Try Again
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeOutlined />}
                onClick={this.handleGoHome}
              >
                Go to Home
              </Button>
            </Box>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}
